import React, { useEffect, useState } from 'react';
import { Alert, Drawer, Empty, List, Skeleton, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

import { adminPresencasService } from '../../services/api';

dayjs.locale('pt-br');

const { Title, Text } = Typography;

const STATUS_LABELS = {
  REALIZADA: { texto: 'Realizada', cor: 'green' },
  PENDENTE: { texto: 'Pendente', cor: 'orange' },
  FUTURA: { texto: 'Futura', cor: 'blue' },
  EM_ANDAMENTO: { texto: 'Em andamento', cor: 'processing' },
  CANCELADA: { texto: 'Cancelada', cor: 'red' },
};

const STATUS_ALUNO_LABELS = {
  PRESENTE: 'Presente',
  AUSENTE: 'Ausente',
  JUSTIFICADA: 'Falta justificada',
};

const resolverErroDetalhe = (error) => {
  const status = error?.response?.status;
  if (status === 404) return 'Esta chamada não foi encontrada ou não está disponível para você.';
  if (status === 403) return 'Você não possui acesso a esta área.';
  return 'Não foi possível carregar o detalhe da chamada. Tente novamente.';
};

const AdminPresencasDetalheDrawer = ({ encontroId, onClose }) => {
  const [detalhe, setDetalhe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (!encontroId) {
      setDetalhe(null);
      setErro('');
      return;
    }

    let cancelado = false;
    setLoading(true);
    setErro('');

    adminPresencasService
      .obterDetalheChamada(encontroId)
      .then((response) => {
        if (!cancelado) setDetalhe(response.data || null);
      })
      .catch((error) => {
        if (!cancelado) setErro(resolverErroDetalhe(error));
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });

    return () => {
      cancelado = true;
    };
  }, [encontroId]);

  const statusInfo = detalhe ? STATUS_LABELS[detalhe.status_chamada] || { texto: detalhe.status_chamada, cor: 'default' } : null;

  return (
    <Drawer
      title="Detalhe da chamada"
      open={Boolean(encontroId)}
      onClose={onClose}
      width={480}
      destroyOnClose
    >
      {loading && (
        <div role="status" aria-live="polite">
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      )}

      {!loading && erro && (
        <Alert type="error" showIcon message={erro} />
      )}

      {!loading && !erro && detalhe && (
        <div className="admin-presencas-detalhe">
          <Title level={4}>{detalhe.turma?.nome}</Title>
          <Text type="secondary">
            {detalhe.unidade?.nome} · {detalhe.modalidade?.nome}
          </Text>
          <div className="admin-presencas-detalhe__meta">
            <Text>{dayjs(detalhe.data).format('DD/MM/YYYY')}</Text>
            <Text>{String(detalhe.hora_inicio).slice(0, 5)}–{String(detalhe.hora_fim).slice(0, 5)}</Text>
            <Text>Professor: {detalhe.professor?.nome}</Text>
          </div>

          {statusInfo && <Tag color={statusInfo.cor}>{statusInfo.texto}</Tag>}

          <div className="admin-presencas-detalhe__resumo" role="status" aria-live="polite">
            <Text>{detalhe.total_alunos} aluno(s)</Text>
            <Text>{detalhe.presentes} presente(s)</Text>
            <Text>{detalhe.ausentes} ausência(s)</Text>
            <Text>{detalhe.justificadas} falta(s) justificada(s)</Text>
            {detalhe.percentual_presenca !== null && detalhe.percentual_presenca !== undefined && (
              <Text strong>{detalhe.percentual_presenca.toLocaleString('pt-BR', { minimumFractionDigits: 1 })}% de frequência</Text>
            )}
          </div>

          {detalhe.status_chamada !== 'REALIZADA' && (
            <Alert
              type="info"
              showIcon
              message="Chamada ainda não realizada"
              className="admin-presencas-detalhe__aviso"
            />
          )}

          {detalhe.alunos.length === 0 ? (
            <Empty description="Nenhum aluno elegível para esta turma." />
          ) : (
            <List
              className="admin-presencas-detalhe__lista-alunos"
              dataSource={detalhe.alunos}
              renderItem={(aluno) => (
                <List.Item>
                  <div className="admin-presencas-detalhe__aluno">
                    <Text>{aluno.nome}</Text>
                    <Text type="secondary">
                      {aluno.status ? STATUS_ALUNO_LABELS[aluno.status] || aluno.status : 'Chamada ainda não realizada'}
                    </Text>
                  </div>
                </List.Item>
              )}
            />
          )}
        </div>
      )}
    </Drawer>
  );
};

export default AdminPresencasDetalheDrawer;
